/**
 * Lightweight analytics tracker for VirtualTourEasy viewer
 * Batches events and sends every 5 seconds to reduce API calls
 */

interface AnalyticsEvent {
  tour_uuid: string
  event_type: 'tour_view' | 'scene_view' | 'hotspot_click' | 'share' | 'session_start' | 'session_end' | 'video_view' | 'video_play' | 'video_complete' | 'video_download'
  scene_uuid?: string
  hotspot_id?: string
  session_id: string
  visitor_id: string
  referrer?: string
  duration_seconds?: number
  metadata?: Record<string, any>
}

function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

function getVisitorId(): string {
  const key = 'vte_visitor_id'
  try {
    let id = localStorage.getItem(key)
    if (!id) {
      id = generateUUID()
      localStorage.setItem(key, id)
    }
    return id
  } catch {
    return generateUUID()
  }
}

export class AnalyticsTracker {
  private apiUrl: string
  private tourUuid: string
  private sessionId: string
  private visitorId: string
  private eventQueue: AnalyticsEvent[] = []
  private flushTimer: number | null = null
  private sessionStart: number
  private lastSceneUuid: string | null = null
  private lastSceneTime: number = 0
  private destroyed = false

  constructor(apiUrl: string, tourUuid: string) {
    this.apiUrl = apiUrl.replace(/\/$/, '')
    this.tourUuid = tourUuid
    this.sessionId = generateUUID()
    this.visitorId = getVisitorId()
    this.sessionStart = Date.now()

    // Start flush interval
    this.flushTimer = window.setInterval(() => this.flush(), 5000)

    // Send session_end on unload
    this.handleUnload = this.handleUnload.bind(this)
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this)
    window.addEventListener('beforeunload', this.handleUnload)
    document.addEventListener('visibilitychange', this.handleVisibilityChange)
  }

  trackTourView(): void {
    this.enqueue('session_start')
    this.enqueue('tour_view')
  }

  trackSceneView(sceneUuid: string): void {
    // Record duration for previous scene
    if (this.lastSceneUuid && this.lastSceneTime > 0) {
      const duration = Math.round((Date.now() - this.lastSceneTime) / 1000)
      this.enqueue('scene_view', {
        scene_uuid: this.lastSceneUuid,
        duration_seconds: duration,
      })
    }
    this.lastSceneUuid = sceneUuid
    this.lastSceneTime = Date.now()
  }

  trackHotspotClick(hotspotId: string, sceneUuid?: string): void {
    this.enqueue('hotspot_click', { hotspot_id: hotspotId, scene_uuid: sceneUuid })
  }

  trackShare(platform: string): void {
    this.enqueue('share', { metadata: { platform } })
    this.flush() // Flush immediately — user may leave after sharing
  }

  /** Video page/player events. Flushed immediately — viewers often leave right after. */
  trackVideo(eventType: 'video_view' | 'video_play' | 'video_complete' | 'video_download', videoUuid: string): void {
    this.enqueue(eventType, { metadata: { video_uuid: videoUuid } })
    this.flush()
  }

  getAttributionContext(): { session_id: string; visitor_id: string; referrer?: string } {
    return {
      session_id: this.sessionId,
      visitor_id: this.visitorId,
      referrer: document.referrer || undefined,
    }
  }

  /**
   * Inside an iframe, document.referrer is the page hosting the embed — record it as host_page so
   * embedded traffic can be broken out from direct views (for tours and videos alike).
   */
  private embedContext(): Record<string, any> | null {
    try {
      if (window.self !== window.top) {
        return { embedded: true, host_page: (document.referrer || '').slice(0, 500) || null }
      }
      return null
    } catch {
      return { embedded: true, host_page: null } // cross-origin frame access can throw — still an embed
    }
  }

  private enqueue(
    eventType: AnalyticsEvent['event_type'],
    extra: Partial<AnalyticsEvent> = {}
  ): void {
    if (this.destroyed) return
    const embed = this.embedContext()
    const metadata = embed || extra.metadata ? { ...(embed || {}), ...(extra.metadata || {}) } : undefined
    this.eventQueue.push({
      tour_uuid: this.tourUuid,
      event_type: eventType,
      session_id: this.sessionId,
      visitor_id: this.visitorId,
      referrer: (document.referrer || '').slice(0, 500) || undefined,
      ...extra,
      metadata,
    })
  }

  private async flush(): Promise<void> {
    if (this.eventQueue.length === 0) return
    const events = this.eventQueue.splice(0)

    try {
      await fetch(`${this.apiUrl}/api/analytics/events`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ events }),
        keepalive: true,
      })
    } catch {
      // Silently fail — analytics should never break the viewer
    }
  }

  private handleUnload(): void {
    this.sendSessionEnd()
    this.flush()
  }

  private handleVisibilityChange(): void {
    if (document.visibilityState === 'hidden') {
      this.sendSessionEnd()
      this.flush()
    }
  }

  private sendSessionEnd(): void {
    // Final scene duration
    if (this.lastSceneUuid && this.lastSceneTime > 0) {
      const duration = Math.round((Date.now() - this.lastSceneTime) / 1000)
      this.enqueue('scene_view', {
        scene_uuid: this.lastSceneUuid,
        duration_seconds: duration,
      })
      this.lastSceneUuid = null
    }

    const totalDuration = Math.round((Date.now() - this.sessionStart) / 1000)
    this.enqueue('session_end', { duration_seconds: totalDuration })
  }

  destroy(): void {
    this.destroyed = true
    if (this.flushTimer) {
      clearInterval(this.flushTimer)
      this.flushTimer = null
    }
    this.sendSessionEnd()
    this.flush()
    window.removeEventListener('beforeunload', this.handleUnload)
    document.removeEventListener('visibilitychange', this.handleVisibilityChange)
  }
}
